/**
 * Escalation rules — what raises a handoff to a person, and whose phone it
 * rings. Distinct from guidance (how Iris talks) and knowledge (what it knows):
 * a rule is routing.
 *
 * Proxied through operations at `/api/messaging/iris/escalation-rules`
 * (operations is the FE's auth boundary + RBAC gate; it forwards to
 * ai-operations with the shared service key). Powers Settings → Iris → Handoff.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, operationsClient } from '@/lib/api';

const BASE = '/api/messaging/iris/escalation-rules';

/** Must match ESCALATION_TRIGGER_TYPES in ai-operations exactly. */
export const ESCALATION_TRIGGERS = [
  'HUMAN_REQUEST',
  'NEGATIVE_SENTIMENT',
  'PRICING',
  'NO_SLOTS',
  'LOW_CONFIDENCE',
  'RESCHEDULE_THRESHOLD',
] as const;
export type EscalationTrigger = (typeof ESCALATION_TRIGGERS)[number];

/** Written for operators, not for the enum. */
export const ESCALATION_TRIGGER_LABELS: Record<EscalationTrigger, string> = {
  HUMAN_REQUEST: 'Asks for a person',
  NEGATIVE_SENTIMENT: 'Unhappy customer',
  PRICING: 'Haggling over price',
  NO_SLOTS: 'Nothing available to book',
  LOW_CONFIDENCE: 'Iris is unsure',
  RESCHEDULE_THRESHOLD: 'Rescheduled too often',
};

export const ESCALATION_TRIGGER_HINTS: Record<EscalationTrigger, string> = {
  HUMAN_REQUEST: 'The customer explicitly asked to speak to somebody.',
  NEGATIVE_SENTIMENT: 'They complained about the service or sounded upset.',
  PRICING: 'They pushed for a discount rather than just asking the price.',
  NO_SLOTS: 'The calendar had nothing they could take.',
  LOW_CONFIDENCE: 'Iris could not answer safely and stopped.',
  RESCHEDULE_THRESHOLD: 'They have moved this appointment more than allowed.',
};

/** Bounds mirrored from the backend validator. */
export const MAX_RULE_NAME_LENGTH = 120;
export const MAX_PHRASE_LENGTH = 120;
export const MAX_PHRASES = 50;
export const MAX_RUNGS = 5;
/** Per STEP, not per list — everyone on a step rings at once. */
export const MAX_RUNG_MEMBERS = 25;
export const MIN_RING_SECONDS = 5;
export const MAX_RING_SECONDS = 120;
export const DEFAULT_RING_SECONDS = 25;

/** One step of the ring ladder. Everyone here rings together. */
export interface EscalationRung {
  userIds: string[];
  externalNumbers: string[];
  ringSeconds: number;
}

export type EscalationFallback = 'VOICEMAIL' | 'HANGUP';

export interface EscalationRule {
  id: string;
  name: string;
  triggerTypes: EscalationTrigger[];
  phrases: string[];
  channels: string[];
  enabled: boolean;
  sortOrder: number;
  targets: EscalationRung[];
  fallback: EscalationFallback;
}

export interface CreateEscalationRulePayload {
  name: string;
  triggerTypes: EscalationTrigger[];
  phrases?: string[];
  channels?: string[];
  enabled?: boolean;
  targets?: EscalationRung[];
  fallback?: EscalationFallback;
}

/**
 * No `sortOrder`: precedence is a property of the whole list and changes only
 * through `useReorderEscalationRules`, which the API applies in one transaction.
 */
export type UpdateEscalationRulePayload = Partial<CreateEscalationRulePayload>;

export const escalationRulesKey = ['messaging', 'iris-escalation-rules'] as const;

export function useEscalationRules() {
  return useQuery({
    queryKey: escalationRulesKey,
    queryFn: () => apiGet<EscalationRule[]>(operationsClient, BASE),
    staleTime: 30_000,
  });
}

export function useCreateEscalationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateEscalationRulePayload) =>
      apiPost<EscalationRule, CreateEscalationRulePayload>(operationsClient, BASE, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: escalationRulesKey });
    },
  });
}

export function useUpdateEscalationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & UpdateEscalationRulePayload) =>
      apiPatch<EscalationRule, UpdateEscalationRulePayload>(
        operationsClient,
        `${BASE}/${encodeURIComponent(id)}`,
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: escalationRulesKey });
    },
  });
}

/**
 * Set the order of every rule at once.
 *
 * A whole-list write rather than two swaps: swapping leaves a window where both
 * rules hold the same position, and a call landing in it is resolved by creation
 * order — exactly what the operator was changing.
 */
export function useReorderEscalationRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiPut<void, { ids: string[] }>(operationsClient, `${BASE}/reorder`, { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: escalationRulesKey });
    },
  });
}

export function useDeleteEscalationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<void>(operationsClient, `${BASE}/${encodeURIComponent(id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: escalationRulesKey });
    },
  });
}

/** E.164, matching what the backend will actually accept and dial. */
export function isDialableNumber(value: string): boolean {
  return /^\+[1-9]\d{6,14}$/.test(value.trim());
}

/**
 * How a rung reads in one line: "Sam and Alex · 20s". Used in the collapsed rule
 * row, where the point is to see who a rule rings without opening it.
 */
export function describeRung(
  rung: EscalationRung,
  nameFor: (userId: string) => string,
): string {
  const people = rung.userIds.map(nameFor);
  const all = [...people, ...rung.externalNumbers];
  if (all.length === 0) return 'nobody';
  if (all.length === 1) return all[0];
  if (all.length === 2) return `${all[0]} and ${all[1]}`;
  return `${all[0]}, ${all[1]} and ${all.length - 2} more`;
}
